import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Pencil, Trash2, Image, Palette, Users,
  DollarSign, BarChart3, Grid3x3, ChevronRight, Save, User, Upload
} from "lucide-react";
import { API } from "@/App";

export default function AdminPage({ user, onLogout }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState("collections");
  const [stats, setStats] = useState({});
  const [collections, setCollections] = useState([]);
  const [wallpapers, setWallpapers] = useState([]);
  const [users, setUsers] = useState([]);
  const [prices, setPrices] = useState({ baseline_price: 122, artist_price: 153 });

  // Dialog states
  const [colDialogOpen, setColDialogOpen] = useState(false);
  const [wpDialogOpen, setWpDialogOpen] = useState(false);
  const [editingCol, setEditingCol] = useState(null);
  const [editingWp, setEditingWp] = useState(null);

  const [colForm, setColForm] = useState({
    name: "", collection_type: "baseline", artist_name: "", description: "", cover_image_url: "", price_per_sqm: ""
  });
  const [wpForm, setWpForm] = useState({
    collection_id: "", name: "", description: "", image_url: "", thumbnail_url: ""
  });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [statsRes, colRes, wpRes, pricesRes] = await Promise.all([
        axios.get(`${API}/api/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/api/collections`, { withCredentials: true }),
        axios.get(`${API}/api/wallpapers`, { withCredentials: true }),
        axios.get(`${API}/api/prices`, { withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setCollections(colRes.data);
      setWallpapers(wpRes.data);
      setPrices(pricesRes.data);
    } catch {}
  };

  const handleSaveCollection = async (e) => {
    e.preventDefault();
    try {
      if (editingCol) {
        await axios.put(`${API}/api/admin/collections/${editingCol.collection_id}`, colForm, { withCredentials: true });
        toast.success("Collezione aggiornata");
      } else {
        await axios.post(`${API}/api/admin/collections`, colForm, { withCredentials: true });
        toast.success("Collezione creata");
      }
      setColDialogOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  const handleDeleteCollection = async (colId) => {
    if (!window.confirm("Eliminare la collezione e tutti i soggetti?")) return;
    try {
      await axios.delete(`${API}/api/admin/collections/${colId}`, { withCredentials: true });
      toast.success("Collezione eliminata");
      fetchAll();
    } catch { toast.error("Errore nell'eliminazione"); }
  };

  const handleSaveWallpaper = async (e) => {
    e.preventDefault();
    try {
      if (editingWp) {
        await axios.put(`${API}/api/admin/wallpapers/${editingWp.wallpaper_id}`, wpForm, { withCredentials: true });
        toast.success("Soggetto aggiornato");
      } else {
        await axios.post(`${API}/api/admin/wallpapers`, wpForm, { withCredentials: true });
        toast.success("Soggetto creato");
      }
      setWpDialogOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  const handleDeleteWallpaper = async (wpId) => {
    if (!window.confirm("Eliminare questo soggetto?")) return;
    try {
      await axios.delete(`${API}/api/admin/wallpapers/${wpId}`, { withCredentials: true });
      toast.success("Soggetto eliminato");
      fetchAll();
    } catch { toast.error("Errore"); }
  };

  const handlePatternUpload = async (wpId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      await axios.post(`${API}/api/admin/wallpapers/${wpId}/upload-pattern`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Pattern caricato!");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore upload pattern");
    }
  };

  const handleCoverUpload = async (colId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      await axios.post(`${API}/api/admin/collections/${colId}/upload-cover`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Copertina caricata!");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore upload copertina");
    }
  };

  const handleUpdatePrices = async () => {
    try {
      await axios.put(`${API}/api/admin/prices`, prices, { withCredentials: true });
      toast.success("Prezzi aggiornati");
      fetchAll();
    } catch { toast.error("Errore aggiornamento prezzi"); }
  };

  const openEditCollection = (col) => {
    setEditingCol(col);
    setColForm({
      name: col.name, collection_type: col.collection_type,
      artist_name: col.artist_name || "", description: col.description || "",
      cover_image_url: col.cover_image_url || "", price_per_sqm: col.price_per_sqm || ""
    });
    setColDialogOpen(true);
  };

  const openEditWallpaper = (wp) => {
    setEditingWp(wp);
    setWpForm({
      collection_id: wp.collection_id, name: wp.name,
      description: wp.description || "", image_url: wp.image_url || "",
      thumbnail_url: wp.thumbnail_url || ""
    });
    setWpDialogOpen(true);
  };

  const tabs = [
    { id: "collections", label: "Collezioni", icon: Grid3x3 },
    { id: "wallpapers", label: "Soggetti", icon: Palette },
    { id: "prices", label: "Prezzi", icon: DollarSign },
    { id: "stats", label: "Statistiche", icon: BarChart3 },
  ];

  return (
    <div className="min-h-screen bg-stone-50" data-testid="admin-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/catalogo")} className="flex items-center gap-2 text-stone-500 hover:text-stone-900">
              <ArrowLeft className="w-4 h-4" />
              <span className="text-xs uppercase tracking-widest hidden sm:inline">Catalogo</span>
            </button>
            <ChevronRight className="w-3 h-3 text-stone-300" />
            <span className="text-sm font-medium text-stone-900">Pannello Admin</span>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Tabs */}
        <div className="flex gap-1 mb-6 overflow-x-auto pb-2">
          {tabs.map(t => (
            <Button
              key={t.id}
              data-testid={`tab-${t.id}`}
              variant={tab === t.id ? "default" : "outline"}
              onClick={() => setTab(t.id)}
              className={`rounded-none text-xs uppercase tracking-widest h-9 px-4 flex-shrink-0 gap-2 ${
                tab === t.id ? "bg-stone-900 text-white" : "border-stone-200"
              }`}
            >
              <t.icon className="w-3.5 h-3.5" />
              {t.label}
            </Button>
          ))}
        </div>

        {/* Collections Tab */}
        {tab === "collections" && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-light">Collezioni ({collections.length})</h2>
              <Button
                data-testid="add-collection-btn"
                onClick={() => {
                  setEditingCol(null);
                  setColForm({ name: "", collection_type: "baseline", artist_name: "", description: "", cover_image_url: "", price_per_sqm: "" });
                  setColDialogOpen(true);
                }}
                className="rounded-none bg-stone-900 text-white text-xs uppercase tracking-widest gap-2"
              >
                <Plus className="w-3.5 h-3.5" /> Nuova Collezione
              </Button>
            </div>
            <div className="grid gap-3">
              {collections.map(col => (
                <div key={col.collection_id} className="bg-white border border-stone-200 p-4 flex items-center gap-4">
                  <div className="w-16 h-12 bg-stone-100 flex-shrink-0 overflow-hidden">
                    {col.cover_image_url ? (
                      <img src={col.cover_image_url} alt="" className="w-full h-full object-cover" />
                    ) : <div className="w-full h-full flex items-center justify-center"><Grid3x3 className="w-4 h-4 text-stone-300" /></div>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{col.name}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 uppercase tracking-widest ${col.collection_type === "artist" ? "bg-amber-50 text-amber-700" : "bg-stone-100 text-stone-500"}`}>
                        {col.collection_type}
                      </span>
                    </div>
                    <p className="text-xs text-stone-500">{col.price_per_sqm?.toFixed(2)} €/mq — {col.wallpaper_count || 0} soggetti</p>
                  </div>
                  <div className="flex gap-1 items-center">
                    <label className="cursor-pointer" data-testid={`upload-cover-btn-${col.collection_id}`}>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleCoverUpload(col.collection_id, file);
                          e.target.value = "";
                        }}
                      />
                      <div className={`h-7 px-2 flex items-center gap-1 text-[9px] uppercase tracking-widest border ${
                        col.cover_image_url ? "border-green-300 text-green-700 bg-green-50 hover:bg-green-100" : "border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100"
                      } transition-colors`}>
                        <Upload className="w-2.5 h-2.5" />
                        {col.cover_image_url ? "Cambia" : "Copertina"}
                      </div>
                    </label>
                    <Button variant="ghost" size="sm" onClick={() => openEditCollection(col)} className="h-8 w-8 p-0">
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteCollection(col.collection_id)} className="h-8 w-8 p-0 text-red-500 hover:text-red-700">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Wallpapers Tab */}
        {tab === "wallpapers" && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-light">Soggetti ({wallpapers.length})</h2>
              <Button
                data-testid="add-wallpaper-btn"
                onClick={() => {
                  setEditingWp(null);
                  setWpForm({ collection_id: collections[0]?.collection_id || "", name: "", description: "", image_url: "", thumbnail_url: "" });
                  setWpDialogOpen(true);
                }}
                className="rounded-none bg-stone-900 text-white text-xs uppercase tracking-widest gap-2"
              >
                <Plus className="w-3.5 h-3.5" /> Nuovo Soggetto
              </Button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {wallpapers.map(wp => {
                const col = collections.find(c => c.collection_id === wp.collection_id);
                return (
                  <div key={wp.wallpaper_id} className="bg-white border border-stone-200 overflow-hidden group">
                    <div className="aspect-square bg-stone-100 overflow-hidden relative">
                      {wp.image_url ? (
                        <img src={wp.image_url} alt={wp.name} className="w-full h-full object-cover" />
                      ) : <div className="w-full h-full flex items-center justify-center"><Image className="w-6 h-6 text-stone-300" /></div>}
                      {wp.pattern_url && (
                        <div className="absolute top-1 right-1 bg-green-600 text-white text-[8px] px-1.5 py-0.5 uppercase tracking-widest">
                          Pattern
                        </div>
                      )}
                    </div>
                    <div className="p-2">
                      <p className="text-xs font-medium truncate">{wp.name}</p>
                      <p className="text-[10px] text-stone-400 truncate">{col?.name}</p>
                      <div className="flex gap-1 mt-2 items-center">
                        <Button variant="ghost" size="sm" onClick={() => openEditWallpaper(wp)} className="h-6 w-6 p-0">
                          <Pencil className="w-3 h-3" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteWallpaper(wp.wallpaper_id)} className="h-6 w-6 p-0 text-red-500">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                        <label className="cursor-pointer ml-auto">
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handlePatternUpload(wp.wallpaper_id, file);
                              e.target.value = "";
                            }}
                          />
                          <div className={`h-6 px-2 flex items-center gap-1 text-[9px] uppercase tracking-widest border ${
                            wp.pattern_url ? "border-green-300 text-green-700 bg-green-50" : "border-amber-300 text-amber-700 bg-amber-50"
                          }`}>
                            <Upload className="w-2.5 h-2.5" />
                            {wp.pattern_url ? "Cambia" : "Pattern"}
                          </div>
                        </label>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Prices Tab */}
        {tab === "prices" && (
          <div className="max-w-md">
            <h2 className="text-lg font-light mb-6">Listino Prezzi al mq</h2>
            <div className="bg-white border border-stone-200 p-6 space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-widest text-stone-500">Baseline (€/mq)</Label>
                <Input
                  data-testid="baseline-price-input"
                  type="number" step="0.01"
                  value={prices.baseline_price || ""}
                  onChange={e => setPrices({...prices, baseline_price: parseFloat(e.target.value) || 0})}
                  className="rounded-none mt-1"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-stone-500">Artist Stripes (€/mq)</Label>
                <Input
                  data-testid="artist-price-input"
                  type="number" step="0.01"
                  value={prices.artist_price || ""}
                  onChange={e => setPrices({...prices, artist_price: parseFloat(e.target.value) || 0})}
                  className="rounded-none mt-1"
                />
              </div>
              <Button
                data-testid="save-prices-btn"
                onClick={handleUpdatePrices}
                className="rounded-none bg-stone-900 text-white w-full gap-2"
              >
                <Save className="w-4 h-4" /> Aggiorna Prezzi
              </Button>
              <p className="text-xs text-stone-400">L'aggiornamento si applica a tutte le collezioni del tipo corrispondente.</p>
            </div>
          </div>
        )}

        {/* Stats Tab */}
        {tab === "stats" && (
          <div>
            <h2 className="text-lg font-light mb-6">Statistiche</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { label: "Utenti", value: stats.total_users || 0, icon: Users },
                { label: "Collezioni", value: stats.total_collections || 0, icon: Grid3x3 },
                { label: "Soggetti", value: stats.total_wallpapers || 0, icon: Palette },
                { label: "Simulazioni", value: stats.total_simulations || 0, icon: BarChart3 },
              ].map(s => (
                <div key={s.label} className="bg-white border border-stone-200 p-5 text-center">
                  <s.icon className="w-5 h-5 text-stone-400 mx-auto mb-2" />
                  <p className="text-2xl font-light text-stone-900">{s.value}</p>
                  <p className="text-xs text-stone-500 uppercase tracking-widest mt-1">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Collection Dialog */}
      <Dialog open={colDialogOpen} onOpenChange={setColDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCol ? "Modifica Collezione" : "Nuova Collezione"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveCollection} className="space-y-4 mt-2">
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500">Nome</Label>
              <Input data-testid="col-name-input" value={colForm.name} onChange={e => setColForm({...colForm, name: e.target.value})} className="rounded-none mt-1" required />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500">Tipo</Label>
              <Select value={colForm.collection_type} onValueChange={v => setColForm({...colForm, collection_type: v})}>
                <SelectTrigger data-testid="col-type-select" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="baseline">Baseline</SelectItem>
                  <SelectItem value="artist">Artist Stripes</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {colForm.collection_type === "artist" && (
              <div>
                <Label className="text-xs uppercase tracking-widest text-stone-500">Artista</Label>
                <Input value={colForm.artist_name} onChange={e => setColForm({...colForm, artist_name: e.target.value})} className="rounded-none mt-1" />
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500">Descrizione</Label>
              <Input value={colForm.description} onChange={e => setColForm({...colForm, description: e.target.value})} className="rounded-none mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500">URL Immagine Cover</Label>
              <Input value={colForm.cover_image_url} onChange={e => setColForm({...colForm, cover_image_url: e.target.value})} className="rounded-none mt-1" placeholder="https://..." />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500">Prezzo €/mq (opzionale)</Label>
              <Input type="number" step="0.01" value={colForm.price_per_sqm} onChange={e => setColForm({...colForm, price_per_sqm: e.target.value})} className="rounded-none mt-1" placeholder="Usa prezzo globale se vuoto" />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setColDialogOpen(false)} className="flex-1 rounded-none">Annulla</Button>
              <Button type="submit" data-testid="save-col-btn" className="flex-1 rounded-none bg-stone-900 text-white">Salva</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Wallpaper Dialog */}
      <Dialog open={wpDialogOpen} onOpenChange={setWpDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingWp ? "Modifica Soggetto" : "Nuovo Soggetto"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveWallpaper} className="space-y-4 mt-2">
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500">Collezione</Label>
              <Select value={wpForm.collection_id} onValueChange={v => setWpForm({...wpForm, collection_id: v})}>
                <SelectTrigger data-testid="wp-col-select" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {collections.map(c => (
                    <SelectItem key={c.collection_id} value={c.collection_id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500">Nome</Label>
              <Input data-testid="wp-name-input" value={wpForm.name} onChange={e => setWpForm({...wpForm, name: e.target.value})} className="rounded-none mt-1" required />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500">URL Immagine</Label>
              <Input data-testid="wp-image-input" value={wpForm.image_url} onChange={e => setWpForm({...wpForm, image_url: e.target.value})} className="rounded-none mt-1" placeholder="https://..." />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500">Descrizione</Label>
              <Input value={wpForm.description} onChange={e => setWpForm({...wpForm, description: e.target.value})} className="rounded-none mt-1" />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setWpDialogOpen(false)} className="flex-1 rounded-none">Annulla</Button>
              <Button type="submit" data-testid="save-wp-btn" className="flex-1 rounded-none bg-stone-900 text-white">Salva</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
